import { driver, DriveStep } from 'driver.js'
import i18n from 'i18next'
import { Classes } from 'jss'

import endUserManual from '../../assets/ai-go-studio-end-user-manual.pdf'
import { Settings } from '../../components/Settings/types'

const TITLE = i18n.t('Projects')

const getDriver = (
  classesDriver: Classes<'driver'>,
  setProjectsCreateUpdateModal: React.Dispatch<React.SetStateAction<boolean>>,
  settings: Settings,
  updateSettings: (settings: Partial<Settings>) => void,
  updatePreference: () => void,
  projectsCreateUpdateModal: boolean
) => {
  const driverSteps: DriveStep[] = [
    {
      popover: {
        title: i18n.t('Welcome to AI-go Studio!'),
        description: `
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css">         
          ${i18n.t(
            'This guided tour will help you understand the main functionalities of AI-go Studio.'
          )}
          <br/><br/>
          ${i18n.t('You can always access the guided tour by clicking on the')}
          <b><i style="font-size:15px" class="fa">&#xf059;</i></b>
          ${i18n.t('icon in the upper right corner.')}
        `,
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '#newProjectButtonId',
      popover: {
        title: TITLE,
        description: `
          ${i18n.t('Click')} 
            <b>${i18n.t('New project')}</b> 
          ${i18n.t('to create a new {{ item }}.', {
            item: i18n.t('project'),
          })}<br/>
          ${i18n.t(
            'You can create as many projects as you want, and each project can contain one or more products.'
          )}`,
        side: 'bottom',
        align: 'start',
        onNextClick: () => {
          setProjectsCreateUpdateModal(true)
        },
      },
    },
    {
      element: '#projectNameId',
      popover: {
        title: TITLE,
        description: i18n.t('Enter the name of the new {{ item }}.', {
          item: i18n.t('project'),
        }),
        side: 'right',
        align: 'start',
        onPrevClick: () => {
          setProjectsCreateUpdateModal(false)
          driverObj.movePrevious()
        },
      },
    },
    {
      element: '#projectDescriptionId',
      popover: {
        title: TITLE,
        description: i18n.t('Enter the description of the new {{ item }}.', {
          item: i18n.t('project'),
        }),
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '#projectPrivateCheckId',
      popover: {
        title: TITLE,
        description: i18n.t(
          'If desired, you can make a project private, ensuring it is accessible only to you.'
        ),
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '#projectCreateButtonId',
      popover: {
        title: TITLE,
        description: `${i18n.t('Click')} <b>${i18n.t('Create')}</b>
        ${i18n.t('to add the new {{ item }} on top of the {{ items }} list.', {
          item: i18n.t('project'),
          items: i18n.t('projects'),
        })}`,
        side: 'right',
        align: 'start',
        onNextClick: () => {
          setProjectsCreateUpdateModal(false)
          driverObj.moveNext()
        },
      },
    },
    {
      element: '#divProjectsListId',
      popover: {
        title: TITLE,
        description: `
          ${i18n.t('Here you can see all your {{ items }}.', {
            items: i18n.t('projects'),
          })} 
          ${i18n.t('Click on a {{ item }} to see its details.', {
            item: i18n.t('project'),
          })}
          ${i18n.t('You can also edit or delete a {{ item }}.', {
            item: i18n.t('project'),
          })}<br/><br/>
          ${i18n.t('Additional information can be found in the')}
          <a target='_blank' href='${endUserManual}'>
            ${i18n.t('End user manual (PDF)')}
          </a>
          <br/><br/>
          <label htmlFor="doNotShowAgainCheckbox">
            <input
                type="checkbox"
                id="doNotShowAgainCheckbox"
                ${settings.preference__show_guided_tour ? '' : 'checked'}
            />
            ${i18n.t('Do not show again')}
          </label>`,
        side: 'bottom',
        align: 'start',
        onPopoverRender: () => {
          document
            .getElementById('doNotShowAgainCheckbox')
            ?.addEventListener('change', () => {
              updateSettings({
                preference__show_guided_tour: !settings.preference__show_guided_tour,
              })
            })
        },
        onPrevClick: () => {
          setProjectsCreateUpdateModal(true)
        },
      },
    },
  ]

  const driverObj = driver({
    showProgress: true,
    steps: driverSteps,
    nextBtnText: i18n.t('Next'),
    prevBtnText: i18n.t('Previous'),
    doneBtnText: i18n.t('Done'),
    popoverClass: classesDriver.driver,
    onPopoverRender: (popover) => {
      const skipButton = document.createElement('button')
      skipButton.id = 'skipButton'
      skipButton.innerText = i18n.t('Skip all')
      popover.footerButtons.appendChild(skipButton)
      skipButton.addEventListener('click', () => {
        const stepsLength = driverObj.getConfig('steps')?.length
        if (stepsLength) {
          driverObj.drive(stepsLength - 1)
        }
        if (projectsCreateUpdateModal) setProjectsCreateUpdateModal(false)
      })
    },
    onDestroyStarted: () => {
      if (driverObj.isLastStep()) {
        updatePreference()
      }
      if (projectsCreateUpdateModal) setProjectsCreateUpdateModal(false)
      driverObj.destroy()
    },
  })

  return driverObj
}

export default getDriver
